import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2, Trash2, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

export function SettingsWhatsAppSection({ isSuperAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [form, setForm] = useState({
    phone_number_id: "",
    access_token: "",
    verify_token: "",
    app_secret: "",
    business_phone_display: "",
  });

  const [links, setLinks] = useState([]);
  const [phone, setPhone] = useState("");
  const [linking, setLinking] = useState(false);
  const [verifyCode, setVerifyCode] = useState(null);
  const [verifyInstr, setVerifyInstr] = useState("");
  const [botPhoneE164, setBotPhoneE164] = useState("");
  const [botPhoneDisplay, setBotPhoneDisplay] = useState("");

  const [testingConn, setTestingConn] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTestConnection = async () => {
    setTestingConn(true);
    setTestResult(null);
    try {
      const r = await apiClient.post("/whatsapp/system-config/test");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, detail: e.response?.data?.detail || "Test fehlgeschlagen." });
    } finally {
      setTestingConn(false);
    }
  };

  const loadCfg = async () => {
    if (!isSuperAdmin) return;
    setLoadingCfg(true);
    try {
      const r = await apiClient.get("/whatsapp/system-config");
      setCfg(r.data);
      setForm((f) => ({
        ...f,
        phone_number_id: r.data.phone_number_id || "",
        business_phone_display: r.data.business_phone_display || "",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte WhatsApp-Config nicht laden.");
    } finally {
      setLoadingCfg(false);
    }
  };

  const loadLinks = async () => {
    try {
      const r = await apiClient.get("/whatsapp/links");
      setLinks(r.data || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadCfg();
    loadLinks();
  }, [isSuperAdmin]); // eslint-disable-line

  const handleSaveCfg = async (e) => {
    e.preventDefault();
    if (!form.access_token || !form.verify_token || !form.app_secret) {
      toast.error("Access-Token, Verify-Token und App-Secret sind Pflicht.");
      return;
    }
    setSavingCfg(true);
    try {
      await apiClient.put("/whatsapp/system-config", form);
      toast.success("WhatsApp-Konfiguration gespeichert.");
      setForm((f) => ({ ...f, access_token: "", verify_token: "", app_secret: "" }));
      await loadCfg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSavingCfg(false);
    }
  };

  const handleStartLink = async () => {
    if (!phone.trim()) return;
    setLinking(true);
    try {
      const r = await apiClient.post("/whatsapp/links/start", { phone: phone.trim() });
      setVerifyCode(r.data.code);
      setVerifyInstr(r.data.instruction);
      setBotPhoneE164(r.data.bot_phone_e164 || "");
      setBotPhoneDisplay(r.data.bot_phone_display || "");
      toast.success("Code generiert — siehe Anweisung unten.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code konnte nicht erstellt werden.");
    } finally {
      setLinking(false);
    }
  };

  // Iter155: Auto-Polling nach Code-Anforderung — erkennt automatisch,
  // sobald der User im WhatsApp VERIFY XXXX gesendet hat, und zeigt Success.
  useEffect(() => {
    if (!verifyCode) return;
    const targetPhone = phone.trim();
    if (!targetPhone) return;
    let cancelled = false;
    const t0 = Date.now();
    const tick = async () => {
      if (cancelled) return;
      // Polling max 10 Minuten (Verify-Code TTL)
      if (Date.now() - t0 > 10 * 60 * 1000) return;
      try {
        const r = await apiClient.get("/whatsapp/links");
        const foundNew = (r.data || []).some((l) => l.phone_e164 === targetPhone && l.verified_at);
        if (foundNew && !cancelled) {
          toast.success(`✅ ${targetPhone} erfolgreich verknüpft!`);
          setVerifyCode(null);
          setVerifyInstr("");
          setBotPhoneE164("");
          setBotPhoneDisplay("");
          setPhone("");
          setLinks(r.data);
          return; // Polling stoppen
        }
      } catch (_e) { /* silent */ }
      setTimeout(tick, 3000);
    };
    // Erster Poll nach 3s (nicht sofort)
    const first = setTimeout(tick, 3000);
    return () => { cancelled = true; clearTimeout(first); };
  }, [verifyCode, phone]);

  const handleDeleteLink = async (phoneE164) => {
    if (!window.confirm(`Verknüpfung für ${phoneE164} entfernen?`)) return;
    try {
      await apiClient.delete(`/whatsapp/links/${encodeURIComponent(phoneE164)}`);
      toast.success("Verknüpfung entfernt.");
      await loadLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopiert.");
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" id="section-whatsapp" data-testid="settings-whatsapp-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-medium text-slate-900">WhatsApp-Bot</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Kunden fotografieren Quittungen direkt in WhatsApp — der Bot lädt sie automatisch
            in die richtige Firma. Keine App, kein Login. Verknüpfung pro Telefonnummer einmalig per 4-stelligem Code.
          </p>
        </div>
      </div>

      {/* System-Config — nur Super-Admin */}
      {isSuperAdmin && (
        <>
          {/* Schritt-für-Schritt Anleitung — IMMER sichtbar */}
          <details className="mb-3 rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="wa-setup-guide" open={!cfg?.configured}>
            <summary className="cursor-pointer text-xs font-semibold text-sky-900 select-none">
              📋 Komplette Anleitung: Wie du den Bot live schaltest (5 Schritte)
            </summary>
            <div className="mt-3 text-[12px] text-slate-700 space-y-3">
              <div>
                <div className="font-semibold text-slate-900 mb-1">1. Meta Developer Account anlegen (kostenlos)</div>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Öffne <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer" className="underline text-sky-700">developers.facebook.com</a> → mit Facebook/Meta-Account einloggen.</li>
                  <li>Tippe auf <strong>"My Apps"</strong> → <strong>"Create App"</strong> → Typ <strong>"Business"</strong> wählen → Name z.B. "CH-Buchhaltung-Bot".</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-1">2. WhatsApp-Produkt zur App hinzufügen</div>
                <ul className="list-disc ml-5 space-y-1">
                  <li>App-Dashboard → links unter <strong>"Add products"</strong> → <strong>WhatsApp</strong> → "Set up".</li>
                  <li>Meta erstellt automatisch eine Test-Telefonnummer (gratis, 5 Empfänger erlaubt).</li>
                  <li>Für Produktion: <strong>"Add phone number"</strong> → eigene CH-Nummer eintragen → SMS-Code-Verifikation. Beste Wahl: separate VoIP-Nummer (Twilio, Vonage ~CHF 5/Mt) oder ungenutzte Schweizer Nummer.</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-1">3. Token & IDs aus Meta kopieren</div>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Phone Number ID</strong>: WhatsApp → API Setup → "From" Drop-down → der lange Zahlencode darunter (NICHT die Telefonnummer selbst).</li>
                  <li><strong>Access Token</strong>: <span className="text-amber-800 font-semibold">Wichtig — der angezeigte Token in API Setup gilt nur 24 h!</span> Für Produktion: links → <strong>System Users</strong> → "Add" → Rolle "Admin" → "Generate New Token" → Berechtigungen <code className="font-mono text-[11px]">whatsapp_business_messaging</code> + <code className="font-mono text-[11px]">whatsapp_business_management</code> wählen → "Never Expire". Diesen permanenten Token hier eintragen.</li>
                  <li><strong>App Secret</strong>: App-Dashboard → links <strong>Settings → Basic</strong> → "App Secret" → "Show" → kopieren.</li>
                  <li><strong>Verify Token</strong>: Selbst frei wählen (Beispiel: 32 zufällige Zeichen, z.B. via 1Password Generator). Du erfindest den Wert — Meta merkt ihn sich nur.</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-1">4. Webhook in Meta hinterlegen</div>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Im Meta-Dashboard: WhatsApp → <strong>Configuration</strong> → "Webhook" → "Edit".</li>
                  <li>Trage <strong>Callback URL</strong> + <strong>Verify Token</strong> ein (siehe grüne Box unten zum Kopieren). "Verify and Save".</li>
                  <li>Bei "Webhook fields": <strong>messages</strong> abonnieren → "Subscribe".</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-1">5. Hier eintragen + Verbindung testen</div>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Werte unten in "Bot-Konfiguration" ausfüllen → <strong>Speichern</strong>.</li>
                  <li><strong>Verbindung testen</strong> klicken → Bot ruft Meta Graph API mit deinem Access-Token auf → ✅ "Phone-Number-ID gültig" oder ❌ Fehlermeldung.</li>
                  <li>Eigene Mobilnummer unten verknüpfen → <code className="font-mono text-[11px]">VERIFY 1234</code> an Bot senden → fertig.</li>
                </ul>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                <span className="font-semibold">💸 Kosten:</span> Meta zählt erst ab User-initiierten Konversationen. Eingehende Foto-Belege deiner Kunden sind <strong>gratis in den ersten 24 h</strong> (Customer-Initiated). Antworten des Bots im selben 24h-Fenster ebenfalls. Erst zeitversetzte Marketing-Templates kosten ~CHF 0.04/Nachricht (CH-Tarif).
              </div>
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-700">
                <span className="font-semibold">🔒 Sicherheit:</span> Alle 4 Tokens werden Fernet-AES-verschlüsselt in MongoDB abgelegt (Master-Key in env <code className="font-mono">POSTFINANCE_MASTER_KEY</code>). Nach dem Speichern werden sie nie mehr im Klartext angezeigt — nur masked (z.B. <code className="font-mono">EAAB…xyz</code>).
              </div>
            </div>
          </details>

          {/* Webhook URL Box — IMMER sichtbar zum Kopieren */}
          <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3" data-testid="wa-webhook-url-box">
            <div className="text-xs font-semibold text-emerald-900 mb-2">🌐 Webhook-URL (in Meta Configuration eintragen)</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-600 w-24 flex-shrink-0">Callback URL:</span>
                <code className="font-mono text-[11px] bg-white border border-emerald-200 rounded px-2 py-1 flex-1 break-all" data-testid="wa-webhook-url-value">
                  {window.location.origin}/api/whatsapp/webhook
                </code>
                <Button type="button" size="sm" variant="outline" className="rounded-full"
                  onClick={() => copy(`${window.location.origin}/api/whatsapp/webhook`)}
                  data-testid="wa-webhook-url-copy">
                  <Copy className="h-3 w-3 mr-1" /> Kopieren
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-600 w-24 flex-shrink-0">Verify Token:</span>
                <code className="font-mono text-[11px] bg-white border border-emerald-200 rounded px-2 py-1 flex-1">
                  {cfg?.configured ? `${cfg.verify_token_masked} (gespeichert)` : "→ unten eintragen, dann Speichern"}
                </code>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-600 w-24 flex-shrink-0">Webhook-Field:</span>
                <code className="font-mono text-[11px] bg-white border border-emerald-200 rounded px-2 py-1">messages</code>
              </div>
            </div>
          </div>

          <details className="mb-4 rounded-lg border border-slate-200 p-3" data-testid="wa-system-config-section" open={!cfg?.configured}>
            <summary className="cursor-pointer text-xs font-semibold text-slate-800 select-none">
              🔧 Bot-Konfiguration (Super-Admin)
              {cfg?.configured && <span className="ml-2 text-[11px] text-emerald-700">✅ konfiguriert</span>}
            </summary>

            <form className="mt-3 space-y-3" onSubmit={handleSaveCfg}>
              {/* Iter120: Inline-Hilfe – wo bekomme ich diese Werte? */}
              <details className="rounded-md border border-sky-200 bg-sky-50 p-2.5" data-testid="wa-cfg-field-help">
                <summary className="cursor-pointer text-[11px] font-semibold text-sky-900 select-none">
                  ❓ Wo finde ich diese Werte bei Meta? (Anleitung)
                </summary>
                <div className="mt-2 space-y-2.5 text-[11px] text-slate-700">
                  <div>
                    <div className="font-semibold text-slate-900">📱 Phone Number ID</div>
                    <div>Meta App-Dashboard → <strong>WhatsApp → API Setup</strong> → unter „From" steht die <code className="font-mono bg-white px-1 rounded">Phone number ID</code> (15-stellige Zahl).</div>
                    <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="underline text-sky-700">→ developers.facebook.com/apps</a>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">☎️ Bot-Telefonnummer (Anzeige)</div>
                    <div>Im gleichen API-Setup-Bildschirm direkt darüber, formatiert als <code className="font-mono bg-white px-1 rounded">+41 22 555 88 99</code>. Diese Nummer schreibst du auch auf Visitenkarten / Webseite — Kunden senden hier ihre Belege hin.</div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">🔑 Access Token (permanent!)</div>
                    <div>Meta Business Suite → <strong>Business Settings → Users → System Users</strong> → „Add" → Rolle <em>Admin</em> → "Add Assets" (deine WhatsApp-App auswählen) → „Generate New Token":</div>
                    <ul className="list-disc ml-5 mt-1 space-y-0.5">
                      <li>Expiration: <strong>Never</strong></li>
                      <li>Permissions: <code className="font-mono bg-white px-1 rounded">whatsapp_business_messaging</code> + <code className="font-mono bg-white px-1 rounded">whatsapp_business_management</code></li>
                    </ul>
                    <div className="text-amber-800 mt-1">⚠️ Der Token in „API Setup" gilt nur 24h — den nicht hier eintragen!</div>
                    <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer" className="underline text-sky-700">→ business.facebook.com/settings/system-users</a>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">🪪 Verify Token (selbst erfunden!)</div>
                    <div>Eine zufällige Zeichenkette von 32+ Zeichen die DU dir ausdenkst (z.B. mit <code className="font-mono bg-white px-1 rounded">openssl rand -hex 32</code> generieren). Wird gleich an zwei Stellen eingetragen: hier UND bei Meta unter <strong>WhatsApp → Configuration → Webhook → Verify Token</strong>.</div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">🔐 App Secret</div>
                    <div>Meta App-Dashboard → <strong>App Settings → Basic</strong> → Feld „App Secret" → "Show" klicken → kopieren.</div>
                    <div className="text-slate-500">Wird benötigt, um Webhook-Signaturen zu prüfen (Schutz gegen gefälschte Requests).</div>
                  </div>
                  <div className="rounded bg-white border border-sky-100 p-2 mt-2">
                    <div className="font-semibold text-slate-900 mb-1">📋 Reihenfolge:</div>
                    <ol className="list-decimal ml-5 space-y-0.5">
                      <li>Meta Developer Account anlegen (gratis) → App vom Typ „Business" erstellen</li>
                      <li>WhatsApp-Produkt zur App hinzufügen → Test-Bot-Nummer wird automatisch generiert</li>
                      <li>Phone Number ID + Bot-Telefon hier eintragen → <em>Speichern</em></li>
                      <li>Verify Token erfinden + hier eintragen → <em>Speichern</em></li>
                      <li>Webhook-URL (oben im grünen Block) + Verify Token bei Meta unter WhatsApp → Configuration eintragen → „Verify and Save"</li>
                      <li>System User mit permanentem Access Token erstellen → hier eintragen → <em>Speichern</em></li>
                      <li>App Secret aus App-Basic-Settings holen → hier eintragen → <em>Speichern</em></li>
                      <li>„Verbindung testen" klicken → bei ✓ ist alles live</li>
                    </ol>
                  </div>
                </div>
              </details>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px]">Phone Number ID</Label>
                  <Input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                    placeholder="z.B. 123456789012345" data-testid="wa-cfg-phone-number-id" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Bot-Telefonnummer (Anzeige)</Label>
                  <Input value={form.business_phone_display} onChange={(e) => setForm({ ...form, business_phone_display: e.target.value })}
                    placeholder="+41 22 555 88 99" data-testid="wa-cfg-display-phone" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Access Token {cfg?.configured && <span className="text-slate-500">(aktuell: {cfg.access_token_masked})</span>}</Label>
                  <Input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                    placeholder={cfg?.configured ? "Leer lassen = unverändert" : "EAA... (permanent System-User-Token)"} data-testid="wa-cfg-access-token" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Verify Token {cfg?.configured && <span className="text-slate-500">({cfg.verify_token_masked})</span>}</Label>
                  <Input type="password" value={form.verify_token} onChange={(e) => setForm({ ...form, verify_token: e.target.value })}
                    placeholder="frei wählbar, 32+ Zeichen" data-testid="wa-cfg-verify-token" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-[11px]">App Secret {cfg?.configured && <span className="text-slate-500">({cfg.app_secret_masked})</span>}</Label>
                  <Input type="password" value={form.app_secret} onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
                    placeholder="Aus Meta App → Settings → Basic" data-testid="wa-cfg-app-secret" />
                </div>
              </div>
              <div className="flex justify-between items-center gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-full"
                  disabled={testingConn || !cfg?.configured}
                  onClick={handleTestConnection}
                  data-testid="wa-cfg-test-button">
                  {testingConn ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : "🔌"} Verbindung testen
                </Button>
                <Button type="submit" size="sm" className="rounded-full bg-slate-900 text-slate-50"
                  disabled={savingCfg || loadingCfg} data-testid="wa-cfg-save-button">
                  {savingCfg ? <Loader2 className="h-3 w-3 animate-spin" /> : "Speichern"}
                </Button>
              </div>
              {testResult && (
                <div className={`text-[11px] rounded-md border px-3 py-2 ${testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-rose-300 bg-rose-50 text-rose-900"}`}
                  data-testid="wa-cfg-test-result">
                  {testResult.ok ? "✅" : "❌"} {testResult.detail}
                </div>
              )}
            </form>
          </details>
        </>
      )}

      {/* User: Eigene Nummer verknüpfen */}
      <div className="rounded-lg border border-slate-200 p-3 mb-3" data-testid="wa-link-form">
        <div className="text-xs font-semibold text-slate-800 mb-2">📱 Eigene WhatsApp-Nummer verknüpfen</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px] space-y-1">
            <Label className="text-[11px]">Mobilnummer (international, +41…)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+41 79 123 45 67" data-testid="wa-link-phone-input" />
          </div>
          <Button type="button" size="sm" className="rounded-full bg-emerald-600 text-white"
            onClick={handleStartLink} disabled={linking || !phone.trim()} data-testid="wa-link-start-button">
            {linking ? <Loader2 className="h-3 w-3 animate-spin" /> : "Code anfordern"}
          </Button>
        </div>

        {verifyCode && (
          <div className="mt-3 rounded-md border-2 border-emerald-300 bg-emerald-50 p-3" data-testid="wa-verify-instructions">
            <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5 mb-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Schritt 2: Im WhatsApp einsenden
            </div>
            <div className="text-[12px] text-slate-700 mb-2">{verifyInstr}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-base bg-white border border-emerald-200 rounded px-3 py-1.5">VERIFY {verifyCode}</code>
              <Button type="button" size="sm" variant="outline" className="rounded-full"
                onClick={() => copy(`VERIFY ${verifyCode}`)} data-testid="wa-verify-copy">
                <Copy className="h-3 w-3 mr-1" /> Kopieren
              </Button>
              {botPhoneE164 && (
                <a
                  href={`https://wa.me/${botPhoneE164.replace(/\D/g, "")}?text=VERIFY%20${verifyCode}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm"
                  data-testid="wa-verify-deeplink"
                >
                  <ExternalLink className="h-3 w-3" /> WhatsApp öffnen
                </a>
              )}
            </div>
            {/* Iter155: QR-Code für direkten Sprung in den WhatsApp-Chat */}
            {botPhoneE164 && (
              <div className="mt-3 flex flex-col sm:flex-row items-center sm:items-start gap-3 p-3 bg-white rounded border border-emerald-200">
                <QRCodeCanvas
                  value={`https://wa.me/${botPhoneE164.replace(/\D/g, "")}?text=VERIFY%20${verifyCode}`}
                  size={128}
                  level="M"
                  includeMargin={true}
                  data-testid="wa-verify-qrcode"
                />
                <div className="text-[11px] text-slate-700 leading-relaxed max-w-xs">
                  <div className="font-semibold text-emerald-900 mb-1">📱 QR-Code mit Handy scannen</div>
                  <div>Öffnet automatisch WhatsApp mit dem korrekten Bot-Chat und dem VERIFY-Code — einfach nur noch <strong>Senden</strong> drücken.</div>
                  {botPhoneDisplay && (
                    <div className="mt-1.5 text-slate-500">Bot-Nummer: <span className="font-mono">{botPhoneDisplay}</span></div>
                  )}
                </div>
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-2">Code läuft in 10 Min ab.</div>
          </div>
        )}
      </div>

      {/* Verknüpfte Nummern */}
      <div className="rounded-lg border border-slate-200 p-3" data-testid="wa-links-list">
        <div className="text-xs font-semibold text-slate-800 mb-2">Verknüpfte Nummern</div>
        {links.length === 0 ? (
          <div className="text-[11px] text-slate-500 italic">Noch keine Nummern verknüpft.</div>
        ) : (
          <ul className="space-y-1.5">
            {links.map((l) => (
              <li key={l.phone_e164} className="flex items-center justify-between gap-2 p-2 rounded bg-slate-50" data-testid={`wa-link-${l.phone_e164}`}>
                <div>
                  <div className="text-sm font-mono text-slate-900">{l.phone_e164}</div>
                  <div className="text-[10px] text-slate-500">
                    {l.verified_at ? `Verknüpft am ${new Date(l.verified_at).toLocaleDateString("de-CH")}` : "—"}
                  </div>
                </div>
                <Button type="button" size="sm" variant="outline" className="rounded-full text-rose-700 hover:bg-rose-50"
                  onClick={() => handleDeleteLink(l.phone_e164)} data-testid={`wa-link-delete-${l.phone_e164}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <details className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="wa-help-section">
        <summary className="cursor-pointer text-xs font-semibold text-sky-900 select-none">
          ℹ️ Was kann der Bot? (Befehle)
        </summary>
        <ul className="mt-2 text-[12px] text-slate-700 space-y-1">
          <li>📷 <strong>Foto schicken</strong> → wird als Beleg erfasst (OCR + KI + Konto-Vorschlag)</li>
          <li><code>/firma</code> → Liste deiner Firmen</li>
          <li><code>/firma 2</code> → Firma 2 als aktiv setzen (24h gültig)</li>
          <li><code>/status</code> → wie viele Belege heute hochgeladen</li>
          <li><code>/hilfe</code> → Befehlsübersicht</li>
        </ul>
      </details>
    </Card>
  );
}
